import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import io from "socket.io-client";
import { SOCKET_URL } from "../config";

const RealTimeContext = createContext();

export const useRealTime = () => {
  const context = useContext(RealTimeContext);
  if (!context) {
    console.error("useRealTime must be used within a RealTimeProvider");
    return {
      subscribe: () => {},
      unsubscribe: () => {},
      triggerUpdate: () => {},
      lastUpdate: null,
    };
  }
  return context;
};

export const RealTimeProvider = ({ children }) => {
  const [lastUpdate, setLastUpdate] = useState(null);
  const subscribersRef = useRef(new Map());
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const subscribe = useCallback((eventType, callback, componentId) => {
    if (!subscribersRef.current.has(eventType)) {
      subscribersRef.current.set(eventType, new Map());
    }
    subscribersRef.current.get(eventType).set(componentId, callback);
  }, []);

  const unsubscribe = useCallback((eventType, componentId) => {
    if (subscribersRef.current.has(eventType)) {
      subscribersRef.current.get(eventType).delete(componentId);
      if (subscribersRef.current.get(eventType).size === 0) {
        subscribersRef.current.delete(eventType);
      }
    }
  }, []);

  const triggerUpdate = useCallback((eventType, data = null) => {
    console.log(`🔄 Triggering real-time update for: ${eventType}`, data);
    setLastUpdate(new Date());

    const eventSubscribers = subscribersRef.current.get(eventType);
    if (eventSubscribers) {
      eventSubscribers.forEach((callback, componentId) => {
        try {
          callback(data);
        } catch (error) {
          console.error(
            `Error in real-time callback for ${componentId}:`,
            error
          );
        }
      });
    }
  }, []);

  // Socket.IO connection setup
  useEffect(() => {
    // Initialize Socket.IO connection with authentication
    const token = localStorage.getItem("token");

    socketRef.current = io(SOCKET_URL, {
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection event handlers
    socketRef.current.on("connect", () => {
      console.log("🔌 Connected to real-time server");
      setIsConnected(true);
    });

    socketRef.current.on("disconnect", () => {
      console.log("🔌 Disconnected from real-time server");
      setIsConnected(false);
    });

    socketRef.current.on("connect_error", (error) => {
      console.error("🔌 Connection error:", error.message);
      setIsConnected(false);

      // Handle authentication errors
      if (
        error.message === "Token expired" ||
        error.message === "Invalid token" ||
        error.message === "Authentication required"
      ) {
        console.warn("⚠️ Socket authentication failed - redirecting to login");
        // Optional: Trigger logout or redirect to login
        // window.location.href = '/login';
      }
    });

    // Listen for real-time events from server
    socketRef.current.on("orderApproved", (data) => {
      console.log("📡 Received orderApproved event:", data);
      triggerUpdate("orderApproved", data);
    });

    socketRef.current.on("invoiceCreated", (data) => {
      console.log("📡 Received invoiceCreated event:", data);
      triggerUpdate("invoiceCreated", data);
    });

    socketRef.current.on("deliveryOrderCreated", (data) => {
      console.log("📡 Received deliveryOrderCreated event:", data);
      triggerUpdate("deliveryOrderCreated", data);
    });

    socketRef.current.on("salesTargetUpdated", (data) => {
      console.log("📡 Received salesTargetUpdated event:", data);
      triggerUpdate("salesTargetUpdated", data);
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [triggerUpdate]);

  const value = useMemo(
    () => ({
      subscribe,
      unsubscribe,
      triggerUpdate,
      lastUpdate,
      isConnected,
    }),
    [subscribe, unsubscribe, triggerUpdate, lastUpdate, isConnected]
  );

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
};
